import type { ReportLayoutConfig } from '../models/layoutConfig.types'

import { BUDGET_ALLOCATION } from './budgetAllocation'
import { CREATIVE_DASHBOARD } from './creativeDashboard'
import { DEMOGRAPHIC_ANALYSIS } from './demographicAnalysis'
import { HOTEL_ADS } from './hotelAds'
import { PRODUCT_PERFORMANCE } from './productPerformance'
import { STORE_ANALYSIS } from './storeAnalysis'

// 08's OWN layouts — the registry's seed table, not a map anyone reads (`layoutRegistry.ts` is
// the single door; see its header for why v3's `AllConfigs.js` could not be ported as a map).
// v3's `HOTEl_ADS` typo dies here — v4 owns the key space, not the wire. The 7 v3 configs coupled
// to a vertical's own components (AppleSearchAds, UserBehavior, FunnelExploration, ProductInsights,
// Benchmark, BenchmarkCreatives, GoogleAdsPlacements) are no longer "deferred": they belong to
// modules 24/25/27 and reach 08 through `registerReportLayout`, never through this file.
//
// HANDOFF PROTOCOL — one key, one owner. Several seeds below still carry `kind: 'pending'`
// sections standing in for a vertical's component (`productPerformance.ts` → `BasketAnalysisChart`,
// `appAcquisition.ts` → `AppAcquisitionCohortArea`). 08 must NOT import those
// components to fill the holes. The vertical's PR moves the whole config out — delete its entry
// here, register it from the owning feature with the real `component` wired in. Re-registering a
// key that is still seeded THROWS, which is what keeps that migration explicit instead of silent.
//
// `TIKTOK_ADS` and `SEARCH_ADS_EXCELLENCE` took that exit in ORP-18922: both are now registered by
// `features/marketing-insights` with their day×hour heatmap sections wired to real components.
export const SEED_REPORT_LAYOUTS = {
  BUDGET_ALLOCATION,
  CREATIVE_DASHBOARD,
  DEMOGRAPHIC_ANALYSIS,
  HOTEL_ADS,
  PRODUCT_PERFORMANCE,
  STORE_ANALYSIS,
} as const satisfies Record<string, ReportLayoutConfig>

/** The keys 08 ships itself. Closed on purpose — `configs/pages.ts` may only cite these. */
export type SeedReportLayoutKey = keyof typeof SEED_REPORT_LAYOUTS

/**
 * DECISION — open string union, not a branded type.
 *
 * A vertical must be able to register `'USER_BEHAVIOR'` or `'BENCHMARK_CREATIVES'`, keys 08 has
 * never heard of, so the key type cannot be `keyof typeof SEED_REPORT_LAYOUTS`. The two candidates
 * were a brand (`string & { __reportLayoutKey: true }`) and this open union. The brand loses:
 * every registrant would have to cast at the call site, casts are unchecked, and it buys nothing
 * — there is no invariant a brand could protect, because the whole point is that 08 cannot
 * enumerate the key space. The `string & Record<never, never>` arm keeps the seed keys as literal
 * autocomplete/typo-check suggestions (TS only widens the union to `string` AFTER offering them)
 * while accepting any string. `Record<never, never>` rather than `{}` — same effect,
 * `@typescript-eslint/no-empty-object-type` clean.
 *
 * Collisions are therefore a RUNTIME concern, and `registerReportLayout` is where they are caught.
 */
export type ReportLayoutKey = SeedReportLayoutKey | (string & Record<never, never>)
